import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { PostedByToggle } from "@/components/PostedByToggle";
import { Skeleton } from "@/components/Skeleton";
import { useAuth } from "@/auth/AuthContext";
import type {
  Client,
  ClientFunnel,
  ClientListResponse,
  ClientStatus,
  TeamMember,
} from "@/types";
import { roleLabel } from "@/types";
import {
  AMENITY_LABELS,
  BUILDING_STATUS_LABELS,
  CONDITION_LABELS,
  FEATURE_LABELS,
  PROJECT_TYPE_LABELS,
} from "@/types";
import { NEIGHBORHOOD_GROUPS } from "@/lib/neighborhoodVocab";

const STATUS_STYLES: Record<ClientStatus, string> = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  paused: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  closed: "border-slate-500/30 bg-slate-500/10 text-slate-400",
};

const PROPERTY_TYPES = [
  "apartment",
  "house",
  "cottage",
  "land",
  "commercial",
] as const;

const PAGE_SIZE = 30;

function formatRange(min: string | null, max: string | null, suffix: string = "") {
  const mn = min ? Number(min) : null;
  const mx = max ? Number(max) : null;
  if (mn === null && mx === null) return null;
  const fmt = (n: number) => n.toLocaleString();
  if (mn !== null && mx !== null) return `${fmt(mn)}–${fmt(mx)}${suffix}`;
  if (mn !== null) return `≥ ${fmt(mn)}${suffix}`;
  return `≤ ${fmt(mx!)}${suffix}`;
}

export function Clients() {
  const { user } = useAuth();
  const [items, setItems] = useState<Client[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ClientStatus | "">("");
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [editing, setEditing] = useState<Client | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Client | null>(null);
  const [funnel, setFunnel] = useState<ClientFunnel | null>(null);

  // Re-fetch funnel whenever the client list reloads — a new / deleted /
  // status-changed client can shift the strip immediately. Cheap call,
  // single round-trip.
  const loadFunnel = useCallback(async () => {
    try {
      const f = await api<ClientFunnel>("/api/clients/funnel");
      setFunnel(f);
    } catch {
      // Non-fatal — strip just stays in skeleton state.
    }
  }, []);

  const load = useCallback(
    async (nextOffset = 0) => {
      try {
        const params = new URLSearchParams();
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(nextOffset));
        if (search.trim()) params.set("q", search.trim());
        if (statusFilter) params.set("status", statusFilter);
        const r = await api<ClientListResponse>(`/api/clients?${params}`);
        setItems(r.items);
        setTotal(r.total);
        setOffset(nextOffset);
        setError(null);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Failed to load clients");
      }
    },
    [search, statusFilter],
  );

  useEffect(() => {
    load(0);
    loadFunnel();
  }, [load, loadFunnel]);

  // Team list — only owners + managers get one. Brokers can only ever assign
  // to themselves, so we don't bother fetching.
  useEffect(() => {
    if (!user) return;
    if (user.role !== "executive_manager" && user.role !== "lead_broker") return;
    api<TeamMember[]>("/api/team")
      .then(setTeamMembers)
      .catch(() => setTeamMembers([]));
  }, [user]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="w-full">
      <PageHeader
        title="Clients"
        subtitle="Buyers and renters you're actively working to place."
        actions={
          <button className="btn-primary" onClick={() => setCreating(true)}>
            + New client
          </button>
        }
      />

      {error && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm mb-4">
          {error}
        </div>
      )}

      <FunnelStrip funnel={funnel} />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          className="input flex-1 min-w-[240px] max-w-md"
          placeholder="Search name, phone, email, notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="input max-w-[160px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ClientStatus | "")}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="closed">Closed</option>
        </select>
        <div className="text-xs text-slate-500 ml-auto">
          {total.toLocaleString()} total
        </div>
      </div>

      {items === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card p-4 space-y-3">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="card p-10 text-center text-slate-500 text-sm">
          No clients yet — use "+ New client" to add one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((c) => (
            <ClientCard
              key={c.id}
              client={c}
              onEdit={() => setEditing(c)}
              onDelete={() => setDeleting(c)}
              onRematch={async () => {
                try {
                  await api(`/api/clients/${c.id}/rematch`, { method: "POST" });
                  await load(offset);
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : "Rematch failed");
                }
              }}
            />
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <div>
            Page {currentPage} of {pages}
          </div>
          <div className="flex gap-2">
            <button
              className="btn-secondary"
              disabled={offset === 0}
              onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
            >
              Prev
            </button>
            <button
              className="btn-secondary"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => load(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {(creating || editing) && (
        <ClientFormModal
          client={editing}
          teamMembers={teamMembers}
          viewerRole={
            // Shareholder is a full-access apex — behave as executive here.
            user?.role === "shareholder" ? "executive_manager" : (user?.role ?? "broker")
          }
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            await load(offset);
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this client?"
        description={
          deleting ? (
            <>
              <span className="font-semibold text-slate-200">
                {deleting.full_name}
              </span>{" "}
              will be removed along with all matched offers. Deals pointing at this
              client will keep working but lose the link.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await api(`/api/clients/${deleting.id}`, { method: "DELETE" });
            setDeleting(null);
            await load(offset);
          } catch (e) {
            setError(e instanceof ApiError ? e.message : "Delete failed");
            setDeleting(null);
          }
        }}
      />
    </div>
  );
}

/** Per-agent colour palette. We hash the user id into one of these so
 *  every agent gets a stable, distinct tone — owners scanning a 30-card
 *  grid can pick out who owns which lead at a glance without reading
 *  the name. Tones are intentionally medium-saturation: bright enough
 *  to register, calm enough not to fight the rest of the card. */
const AGENT_PALETTE = [
  { bg: "bg-accent-500/15", text: "text-accent-200", ring: "ring-accent-500/40" },
  { bg: "bg-emerald-500/15", text: "text-emerald-200", ring: "ring-emerald-500/40" },
  { bg: "bg-amber-500/15", text: "text-amber-200", ring: "ring-amber-500/40" },
  { bg: "bg-sky-500/15", text: "text-sky-200", ring: "ring-sky-500/40" },
  { bg: "bg-violet-500/15", text: "text-violet-200", ring: "ring-violet-500/40" },
  { bg: "bg-rose-500/15", text: "text-rose-200", ring: "ring-rose-500/40" },
  { bg: "bg-fuchsia-500/15", text: "text-fuchsia-200", ring: "ring-fuchsia-500/40" },
  { bg: "bg-teal-500/15", text: "text-teal-200", ring: "ring-teal-500/40" },
] as const;

const UNASSIGNED_TONE = {
  bg: "bg-white/[0.04]",
  text: "text-slate-400",
  ring: "ring-white/10",
} as const;

/** Stable hash → palette index. djb2-ish over the user id string —
 *  good enough for an 8-bucket pick with even distribution across
 *  hex-style uuids. */
// Chip-picker for the features/amenities multi-selects in the
// advanced match section. ``tone="hard"`` uses a rose accent so
// must-have selections read visually distinct from the "nice to have"
// emerald-toned soft section, even on long lists.
function ChipPicker({
  options,
  selected,
  onToggle,
  tone,
}: {
  options: Record<string, string>;
  selected: string[];
  onToggle: (key: string) => void;
  tone: "hard" | "soft";
}) {
  const onCls =
    tone === "hard"
      ? "border-rose-500/40 bg-rose-500/10 text-rose-200"
      : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200";
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(options).map(([key, label]) => {
        const on = selected.includes(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(key)}
            className={`px-2.5 py-1 text-[11px] rounded-md border transition ${
              on
                ? onCls
                : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/[0.05]"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}


function paletteFor(id: string | null | undefined) {
  if (!id) return UNASSIGNED_TONE;
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  }
  return AGENT_PALETTE[Math.abs(h) % AGENT_PALETTE.length];
}

/** Compact preference chip — same visual weight as a Market filter chip
 *  so the page feels coherent with how brokers think about offers. */
function PrefChip({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: React.ReactNode;
  tone?: "neutral" | "money" | "place";
}) {
  const toneClasses =
    tone === "money"
      ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-200"
      : tone === "place"
        ? "border-sky-500/25 bg-sky-500/10 text-sky-200"
        : "border-white/10 bg-white/[0.03] text-slate-300";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-md border ${toneClasses}`}
    >
      <span className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <span className="font-medium">{value}</span>
    </span>
  );
}

function ClientCard({
  client: c,
  onEdit,
  onDelete,
  onRematch,
}: {
  client: Client;
  onEdit: () => void;
  onDelete: () => void;
  onRematch: () => void | Promise<void>;
}) {
  // Build the chip list from whatever preferences are set. Empty preferences
  // don't render — keeps cards clean for sparsely-filled clients.
  const chips: React.ReactNode[] = [];
  if (c.deal_type) {
    chips.push(
      <PrefChip
        key="deal"
        label="Deal"
        value={c.deal_type === "sale" ? "Sale" : "Rent"}
      />,
    );
  }
  if (c.property_types?.length) {
    chips.push(
      <PrefChip
        key="prop"
        label="Type"
        value={c.property_types.join(" / ")}
      />,
    );
  }
  const roomsRange = formatRange(
    c.rooms_min != null ? String(c.rooms_min) : null,
    c.rooms_max != null ? String(c.rooms_max) : null,
  );
  if (roomsRange) {
    chips.push(<PrefChip key="rooms" label="Rooms" value={roomsRange} />);
  }
  const bedsRange = formatRange(
    c.bedrooms_min != null ? String(c.bedrooms_min) : null,
    c.bedrooms_max != null ? String(c.bedrooms_max) : null,
  );
  if (bedsRange) {
    chips.push(<PrefChip key="beds" label="Beds" value={bedsRange} />);
  }
  const floorRange = formatRange(
    c.floor_min != null ? String(c.floor_min) : null,
    c.floor_max != null ? String(c.floor_max) : null,
  );
  if (floorRange) {
    chips.push(<PrefChip key="floor" label="Floor" value={floorRange} />);
  }
  const budget = formatRange(
    c.price_min,
    c.price_max,
    c.currency ? ` ${c.currency}` : "",
  );
  if (budget) {
    chips.push(<PrefChip key="budget" label="Budget" value={budget} tone="money" />);
  }
  const area = formatRange(c.area_min, c.area_max, " m²");
  if (area) {
    chips.push(<PrefChip key="area" label="Area" value={area} />);
  }
  if (c.districts?.length) {
    const shown = c.districts.slice(0, 3).join(", ");
    const extra = c.districts.length - 3;
    chips.push(
      <PrefChip
        key="districts"
        label="Areas"
        value={extra > 0 ? `${shown} +${extra}` : shown}
        tone="place"
      />,
    );
  }
  if (c.building_statuses?.length) {
    chips.push(
      <PrefChip
        key="building"
        label="Building"
        value={c.building_statuses
          .map((s) => BUILDING_STATUS_LABELS[s] || s)
          .join(", ")}
      />,
    );
  }
  if (c.conditions?.length) {
    chips.push(
      <PrefChip
        key="condition"
        label="Condition"
        value={c.conditions.map((s) => CONDITION_LABELS[s] || s).join(", ")}
      />,
    );
  }

  const agent = c.assigned_user;
  const tone = paletteFor(agent?.id);
  const agentLabel = agent?.full_name || agent?.email || "Unassigned";
  const agentInitials = (agent?.full_name || agent?.email || "·")
    .split(/[\s@]+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="card p-0 group/card relative cursor-pointer hover:border-accent-500/30 transition-colors flex flex-col overflow-hidden"
      onClick={onEdit}
    >
      {/* Agent ribbon — colored band naming the agent who owns this lead.
          Stable colour per user id so a manager scanning a 30-card grid
          can identify who's behind each card without reading. */}
      <div
        className={`flex items-center gap-2 px-4 py-2 ${tone.bg} ${tone.text} border-b border-white/5`}
      >
        <span
          className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold tracking-tight bg-ink-950/40 ring-1 ${tone.ring} flex-shrink-0`}
        >
          {agentInitials || "·"}
        </span>
        <span
          className="text-[12px] font-semibold truncate"
          title={agent?.email || agentLabel}
        >
          {agentLabel}
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-wider opacity-70">
          {agent ? "agent" : "—"}
        </span>
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1">
      {/* Client identity row */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <Link
            to={`/clients/${c.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-slate-100 font-semibold truncate hover:text-accent-200 block"
            title="Open match view"
          >
            {c.full_name}
          </Link>
          <div className="text-[11px] text-slate-500 flex items-center gap-x-2 gap-y-0.5 flex-wrap mt-0.5">
            {c.phone && (
              <span
                className="tabular-nums"
                // When the broker's typed input differs from the
                // canonical E.164 stored in ``phone``, show the raw
                // string on hover so the trail back to the source is
                // always one tooltip away.
                title={
                  c.phone_raw && c.phone_raw.replace(/\s|-/g, "") !== c.phone
                    ? `Typed: ${c.phone_raw}`
                    : undefined
                }
              >
                {c.phone}
              </span>
            )}
            {c.email && (
              <span className="truncate max-w-[18ch]" title={c.email}>
                · {c.email}
              </span>
            )}
            {c.source && <span className="text-slate-600">· {c.source}</span>}
          </div>
        </div>
        <span className={`badge ${STATUS_STYLES[c.status]}`}>{c.status}</span>
      </div>

      {/* Preference chips */}
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">{chips}</div>
      ) : (
        <div className="text-[11px] text-slate-600 italic">
          No preferences set yet — open the card to add some.
        </div>
      )}

      {/* Notes preview — first ~140 chars, italic, low-contrast so it sits
          under the structured chips. */}
      {c.notes && (
        <div className="text-[12px] text-slate-400 italic line-clamp-2 leading-relaxed border-t border-white/[0.04] pt-2">
          “{c.notes.length > 160 ? `${c.notes.slice(0, 160)}…` : c.notes}”
        </div>
      )}

      {/* Footer: full-width match CTA + hover-revealed actions. The CTA
          dominates because opening the match feed is the single most
          common thing brokers do from this card; the side actions are
          opt-in. */}
      <div className="mt-auto flex items-center gap-1.5 pt-2 border-t border-white/[0.04]">
        <Link
          to={`/clients/${c.id}`}
          onClick={(e) => e.stopPropagation()}
          className={`flex-1 inline-flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${
            c.new_match_count > 0
              ? "text-accent-100 bg-accent-500/20 border border-accent-500/40 hover:bg-accent-500/30 shadow-sm shadow-accent-500/10"
              : "text-slate-300 bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:text-slate-100"
          }`}
          title={
            c.new_match_count > 0
              ? `${c.new_match_count} fresh match${c.new_match_count === 1 ? "" : "es"} — open match view`
              : "Open match view"
          }
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2l1.6 5L19 8l-5.2 1.8L12 14l-1.8-4.2L5 8l5.4-1L12 2z" />
          </svg>
          <span>
            {c.new_match_count > 0 ? (
              <>
                Matched <span className="tabular-nums">{c.new_match_count}</span>{" "}
                offer{c.new_match_count === 1 ? "" : "s"}
              </>
            ) : (
              "No matched offers yet"
            )}
          </span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="opacity-60"
          >
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </Link>

        {/* Hover-only secondary actions on desktop; always visible on touch
            (no hover state) and on focus for keyboard / screen-reader users. */}
        <div className="flex items-center gap-1 opacity-100 lg:opacity-0 lg:group-hover/card:opacity-100 focus-within:opacity-100 transition-opacity flex-shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void onRematch();
            }}
            className="text-[11px] font-medium text-accent-300/80 hover:text-accent-200 px-2 py-2 rounded hover:bg-accent-500/10"
            title="Recompute AI matches"
          >
            ✨
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-[11px] font-medium text-rose-300/80 hover:text-rose-200 px-2 py-2 rounded hover:bg-rose-500/10"
            title="Delete client"
          >
            ✕
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

/** Cascading City → Districts → Areas picker. Replaces the old
 *  comma-separated free-text districts field with a structured selection
 *  that maps directly onto the matcher's canonical codes.
 *
 *  Two-level interaction:
 *  - Pick one or more districts (e.g. "Vake / Saburtalo"). Each district
 *    has an admin-region code the matcher will fan out to every member
 *    area when no specific areas are chosen.
 *  - Drill into a district to pick specific areas (Vake, Saburtalo, …).
 *    Picking any area for a district replaces the broad admin code with
 *    the narrower area selection (the parent district auto-deselects).
 *
 *  ``city`` is "Tbilisi" only for now — shown as a static badge so the
 *  hierarchy is visible. When more cities arrive this becomes a real
 *  dropdown.
 */
interface CatalogStreet {
  id: string;
  name: string;
  name_normalized: string;
}
interface CatalogUrban {
  name: string;
  name_normalized: string;
  canonical_code: string | null;
  streets: CatalogStreet[];
}
interface CatalogDistrict {
  name: string;
  name_normalized: string;
  urbans: CatalogUrban[];
}

function LocationPicker({
  districtAdminCodes,
  areaCodes,
  streetIds,
  onChange,
  onChangeStreets,
  legacyDistricts,
  onClearLegacy,
}: {
  districtAdminCodes: Set<string>;
  areaCodes: Set<string>;
  streetIds: Set<string>;
  onChange: (admin: Set<string>, areas: Set<string>) => void;
  onChangeStreets: (streets: Set<string>) => void;
  legacyDistricts: string[];
  onClearLegacy: () => void;
}) {
  // Lazy-load the catalog tree so the modal opens fast for the common
  // case (broker doesn't drill into streets). Single fetch — the tree
  // is small (a few hundred KB serialized) and rarely changes.
  const [catalog, setCatalog] = useState<CatalogDistrict[] | null>(null);
  const [streetsExpanded, setStreetsExpanded] = useState<boolean>(
    () => streetIds.size > 0,
  );
  useEffect(() => {
    if (catalog !== null || !streetsExpanded) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ districts: CatalogDistrict[] }>(
          "/api/catalog/locations",
        );
        if (!cancelled) setCatalog(r.districts);
      } catch {
        if (!cancelled) setCatalog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [streetsExpanded, catalog]);
  // Build the candidate-urbans list once we know the catalog and the
  // current area selection. An urban appears in the streets picker if
  // either (a) the broker picked its specific area code, or (b) the
  // broker picked the parent district as a whole. The latter case is
  // rarer (broker explicitly wants every street in the admin region)
  // so we still respect their pick instead of forcing them to drill.
  const candidateUrbans = (catalog ?? []).flatMap((d) =>
    d.urbans
      .filter((u) => {
        if (u.canonical_code && areaCodes.has(u.canonical_code)) return true;
        // ``district.name_normalized`` doesn't directly equal the admin
        // code (Georgian vs canonical-string), so we approximate via the
        // first urban's canonical code's group lookup. NEIGHBORHOOD_GROUPS
        // is the bridge: if any item code in any selected admin group
        // matches an urban's canonical_code, the urban belongs to it.
        for (const adminCode of districtAdminCodes) {
          const group = NEIGHBORHOOD_GROUPS.find((g) => g.adminCode === adminCode);
          if (group && u.canonical_code) {
            if (group.items.some((i) => i.value === u.canonical_code)) return true;
          }
        }
        return false;
      })
      .map((u) => ({ districtName: d.name, urban: u })),
  );

  const toggleStreet = (id: string) => {
    const next = new Set(streetIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChangeStreets(next);
  };
  const clearStreetsForUrban = (u: CatalogUrban) => {
    const next = new Set(streetIds);
    for (const s of u.streets) next.delete(s.id);
    onChangeStreets(next);
  };
  const selectAllStreetsForUrban = (u: CatalogUrban) => {
    const next = new Set(streetIds);
    for (const s of u.streets) next.add(s.id);
    onChangeStreets(next);
  };
  // Track which district panels are visually expanded — mirrors selection
  // state by default but doesn't strictly equal it (a user can expand a
  // district to peek at its areas without selecting it). Pre-expand any
  // district the user has already chosen.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const out = new Set<string>();
    for (const adm of districtAdminCodes) out.add(adm);
    for (const g of NEIGHBORHOOD_GROUPS) {
      if (g.items.some((i) => areaCodes.has(i.value))) out.add(g.adminCode);
    }
    return out;
  });

  const isDistrictPicked = (adminCode: string, areas: { value: string }[]) =>
    districtAdminCodes.has(adminCode) ||
    areas.some((a) => areaCodes.has(a.value));

  const toggleDistrict = (
    adminCode: string,
    areas: { value: string }[],
  ) => {
    const nextAdmin = new Set(districtAdminCodes);
    const nextAreas = new Set(areaCodes);
    const picked = isDistrictPicked(adminCode, areas);
    if (picked) {
      // Deselecting a district clears both the broad code and any
      // member-area picks — treat the click as "I'm done with this
      // whole district".
      nextAdmin.delete(adminCode);
      for (const a of areas) nextAreas.delete(a.value);
    } else {
      // Selecting fills in the broad admin code; user can drill in
      // afterward to swap to specific areas.
      nextAdmin.add(adminCode);
    }
    onChange(nextAdmin, nextAreas);
    if (!picked) {
      setExpanded((prev) => new Set(prev).add(adminCode));
    }
  };

  const toggleArea = (adminCode: string, areaValue: string) => {
    const nextAdmin = new Set(districtAdminCodes);
    const nextAreas = new Set(areaCodes);
    if (nextAreas.has(areaValue)) {
      nextAreas.delete(areaValue);
    } else {
      nextAreas.add(areaValue);
      // The first area pick within a district replaces the broad
      // admin code with the narrower area selection — otherwise we'd
      // be sending a redundant superset to the matcher.
      nextAdmin.delete(adminCode);
    }
    onChange(nextAdmin, nextAreas);
  };

  const togglePanel = (adminCode: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(adminCode)) next.delete(adminCode);
      else next.add(adminCode);
      return next;
    });
  };

  const totalPicked = districtAdminCodes.size + areaCodes.size + legacyDistricts.length;

  return (
    <div className="space-y-2">
      <div className="label flex items-center gap-2">
        <span>Locations</span>
        {totalPicked > 0 && (
          <span className="text-[10px] text-slate-500 font-normal normal-case tracking-normal">
            {totalPicked} selected
          </span>
        )}
      </div>

      {/* City row — static for now (Tbilisi only). Becomes a dropdown
          once we ingest other cities. */}
      <div className="flex items-center gap-2 text-[12px]">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">
          City
        </span>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-white/10 bg-white/[0.03] text-slate-300">
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6" />
          </svg>
          Tbilisi
        </span>
      </div>

      {/* Districts + areas accordion */}
      <div className="rounded-lg border border-white/10 divide-y divide-white/5 overflow-hidden">
        {NEIGHBORHOOD_GROUPS.map((g) => {
          const picked = isDistrictPicked(g.adminCode, g.items);
          const wholeDistrict = districtAdminCodes.has(g.adminCode);
          const pickedAreas = g.items.filter((a) => areaCodes.has(a.value));
          const isOpen = expanded.has(g.adminCode);
          return (
            <div key={g.adminCode} className={picked ? "bg-accent-500/[0.04]" : ""}>
              <div className="flex items-center px-3 py-2 gap-2">
                <button
                  type="button"
                  onClick={() => toggleDistrict(g.adminCode, g.items)}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    picked ? "text-accent-200" : "text-slate-200 hover:text-accent-200"
                  }`}
                >
                  <span
                    className={`flex items-center justify-center w-4 h-4 rounded border transition ${
                      picked
                        ? "bg-accent-500 border-accent-300 text-ink-950"
                        : "border-slate-500"
                    }`}
                  >
                    {picked && (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  {g.labelKa}
                </button>
                {wholeDistrict && (
                  <span className="text-[10px] text-accent-300/80 uppercase tracking-wider">
                    whole district
                  </span>
                )}
                {pickedAreas.length > 0 && (
                  <span className="text-[10px] text-accent-300/80">
                    {pickedAreas.length} of {g.items.length} areas
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => togglePanel(g.adminCode)}
                  aria-expanded={isOpen}
                  className="ml-auto text-[11px] text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
                  title={isOpen ? "Hide areas" : "Show specific areas"}
                >
                  {isOpen ? "hide areas" : "specific areas"}
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={isOpen ? "rotate-180" : ""}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>
              {isOpen && (
                <div className="px-4 pb-3 pt-1 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {g.items.map((a) => {
                    const areaPicked = areaCodes.has(a.value);
                    return (
                      <button
                        key={a.value}
                        type="button"
                        onClick={() => toggleArea(g.adminCode, a.value)}
                        className={`text-left text-[12px] px-2.5 py-1.5 rounded-md border transition ${
                          areaPicked
                            ? "border-accent-500/40 bg-accent-500/15 text-accent-100"
                            : "border-white/5 bg-white/[0.02] text-slate-300 hover:bg-white/[0.05] hover:border-white/15"
                        }`}
                      >
                        <span
                          className={`inline-block w-3 h-3 mr-1.5 align-middle rounded-sm border ${
                            areaPicked
                              ? "bg-accent-500 border-accent-300"
                              : "border-slate-500"
                          }`}
                        />
                        {a.labelKa}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legacy free-text districts that don't match any canonical code.
          Surfaces them so the broker knows what's saved and can decide to
          drop them in favour of structured picks. Click × to remove. */}
      {legacyDistricts.length > 0 && (
        <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
          <span>Legacy free-text:</span>
          {legacyDistricts.map((d) => (
            <span
              key={d}
              className="px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-200"
            >
              {d}
            </span>
          ))}
          <button
            type="button"
            onClick={onClearLegacy}
            className="text-slate-500 hover:text-rose-300 underline-offset-2 hover:underline"
          >
            Drop all
          </button>
        </div>
      )}

      {/* Streets — third tier under district + neighborhood. Hidden by
          default; the broker can opt in to drill down. Empty selection
          means "no street preference, match any street within the
          chosen districts/areas". */}
      <div className="rounded-lg border border-white/10 overflow-hidden">
        <button
          type="button"
          onClick={() => setStreetsExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-200 hover:bg-white/[0.02]"
        >
          <span className="flex items-center gap-2">
            Streets
            {streetIds.size > 0 && (
              <span className="text-[10px] text-accent-300/80">
                {streetIds.size} selected
              </span>
            )}
            {areaCodes.size === 0 && districtAdminCodes.size === 0 && (
              <span className="text-[10px] text-slate-500 normal-case">
                — pick a district or area first
              </span>
            )}
          </span>
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={streetsExpanded ? "rotate-180 text-slate-400" : "text-slate-500"}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {streetsExpanded && (
          <div className="border-t border-white/5 p-3 space-y-3">
            {catalog === null ? (
              <div className="text-[12px] text-slate-500">Loading catalog…</div>
            ) : candidateUrbans.length === 0 ? (
              <div className="text-[12px] text-slate-500">
                Pick a district or specific area above and the streets inside
                it will appear here.
              </div>
            ) : (
              candidateUrbans.map(({ districtName, urban }) => {
                const pickedHere = urban.streets.filter((s) =>
                  streetIds.has(s.id),
                );
                return (
                  <div key={`${districtName}/${urban.name_normalized}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[12px] text-slate-300">
                        <span className="text-slate-500">{districtName} ›</span>{" "}
                        <span className="font-medium">{urban.name}</span>
                        {pickedHere.length > 0 && (
                          <span className="ml-1.5 text-[10px] text-accent-300/80">
                            {pickedHere.length} of {urban.streets.length}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px]">
                        <button
                          type="button"
                          onClick={() => selectAllStreetsForUrban(urban)}
                          className="text-slate-500 hover:text-accent-300"
                        >
                          all
                        </button>
                        {pickedHere.length > 0 && (
                          <button
                            type="button"
                            onClick={() => clearStreetsForUrban(urban)}
                            className="text-slate-500 hover:text-rose-300"
                          >
                            none
                          </button>
                        )}
                      </div>
                    </div>
                    {urban.streets.length === 0 ? (
                      <div className="text-[11px] text-slate-600 italic">
                        no streets in catalog
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                        {urban.streets.map((s) => {
                          const picked = streetIds.has(s.id);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => toggleStreet(s.id)}
                              className={`text-left text-[11px] px-2 py-1 rounded border transition truncate ${
                                picked
                                  ? "border-accent-500/40 bg-accent-500/15 text-accent-100"
                                  : "border-white/5 bg-white/[0.02] text-slate-400 hover:bg-white/[0.05] hover:border-white/15"
                              }`}
                              title={s.name}
                            >
                              <span
                                className={`inline-block w-2.5 h-2.5 mr-1.5 align-middle rounded-sm border ${
                                  picked
                                    ? "bg-accent-500 border-accent-300"
                                    : "border-slate-600"
                                }`}
                              />
                              {s.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface ClientFormProps {
  client: Client | null;
  teamMembers: TeamMember[];
  viewerRole: "executive_manager" | "operations_manager" | "lead_broker" | "broker" | "member";
  onClose: () => void;
  // ``wasCreate`` lets the parent decide whether to swap into edit-view
  // (to surface matches that are being computed) or just close.
  onSaved: (savedClient?: Client, wasCreate?: boolean) => void | Promise<void>;
}

function ClientFormModal({
  client,
  teamMembers,
  viewerRole,
  onClose,
  onSaved,
}: ClientFormProps) {
  const isEdit = client !== null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const [fullName, setFullName] = useState(client?.full_name ?? "");
  const [phone, setPhone] = useState(client?.phone ?? "");
  const [email, setEmail] = useState(client?.email ?? "");
  const [source, setSource] = useState(client?.source ?? "");
  const [notes, setNotes] = useState(client?.notes ?? "");
  const [status, setStatus] = useState<ClientStatus>(client?.status ?? "active");
  const [assignedUserId, setAssignedUserId] = useState<string>(
    client?.assigned_user_id ?? "",
  );

  const [dealType, setDealType] = useState<"" | "sale" | "rent">(
    client?.deal_type ?? "",
  );
  const [roomsMin, setRoomsMin] = useState<string>(
    client?.rooms_min?.toString() ?? "",
  );
  const [roomsMax, setRoomsMax] = useState<string>(
    client?.rooms_max?.toString() ?? "",
  );
  const [bedroomsMin, setBedroomsMin] = useState<string>(
    client?.bedrooms_min?.toString() ?? "",
  );
  const [bedroomsMax, setBedroomsMax] = useState<string>(
    client?.bedrooms_max?.toString() ?? "",
  );
  const [floorMin, setFloorMin] = useState<string>(
    client?.floor_min?.toString() ?? "",
  );
  const [floorMax, setFloorMax] = useState<string>(
    client?.floor_max?.toString() ?? "",
  );
  const [priceMin, setPriceMin] = useState<string>(client?.price_min ?? "");
  const [priceMax, setPriceMax] = useState<string>(client?.price_max ?? "");
  const [currency, setCurrency] = useState<"" | "USD" | "GEL" | "EUR">(
    client?.currency ?? "USD",
  );
  const [areaMin, setAreaMin] = useState<string>(client?.area_min ?? "");
  const [areaMax, setAreaMax] = useState<string>(client?.area_max ?? "");
  const [propertyTypes, setPropertyTypes] = useState<string[]>(
    client?.property_types ?? [],
  );
  // ── Location picker state ──────────────────────────────────────────────
  // The form stores the selection as two sets: admin-region codes
  // (whole-district selections, like ``vake_saburtalo``) and area codes
  // (individual neighborhoods, like ``vake``). On submit we merge them
  // into ``client.districts``; on edit we split an existing selection
  // back out by checking each saved code against the known admin /
  // area codes from NEIGHBORHOOD_GROUPS.
  const KNOWN_ADMIN = new Set(NEIGHBORHOOD_GROUPS.map((g) => g.adminCode));
  const KNOWN_AREA = new Set(
    NEIGHBORHOOD_GROUPS.flatMap((g) => g.items.map((i) => i.value)),
  );
  const initialAdmin = new Set<string>();
  const initialArea = new Set<string>();
  const initialUnknown: string[] = [];
  for (const raw of client?.districts ?? []) {
    if (KNOWN_ADMIN.has(raw)) initialAdmin.add(raw);
    else if (KNOWN_AREA.has(raw)) initialArea.add(raw);
    else initialUnknown.push(raw);
  }
  const [districtAdminCodes, setDistrictAdminCodes] = useState<Set<string>>(
    initialAdmin,
  );
  const [areaCodes, setAreaCodes] = useState<Set<string>>(initialArea);
  // Picked street IDs (catalog_streets.id, uuid strings). The matcher
  // resolves these to normalized names at query time and ILIKE-matches
  // listing.street. Empty = "no street preference, match any street
  // inside the chosen districts/areas".
  const [streetIds, setStreetIds] = useState<Set<string>>(
    () => new Set(client?.streets ?? []),
  );
  // Anything in the saved list that isn't a known canonical code (legacy
  // free-text entries) we surface as read-only chips so the broker can see
  // them and decide whether to drop them — never silently lose data.
  const [legacyDistricts, setLegacyDistricts] = useState<string[]>(initialUnknown);
  const [buildingStatuses, setBuildingStatuses] = useState<string[]>(
    client?.building_statuses ?? [],
  );
  const [conditions, setConditions] = useState<string[]>(
    client?.conditions ?? [],
  );
  // ── Advanced match dimensions (added 2026-04-28). All optional;
  // leaving everything blank preserves the legacy behaviour.
  const [bathroomsMin, setBathroomsMin] = useState<string>(
    client?.bathrooms_min?.toString() ?? "",
  );
  const [bathroomsMax, setBathroomsMax] = useState<string>(
    client?.bathrooms_max?.toString() ?? "",
  );
  const [projectTypes, setProjectTypes] = useState<string[]>(
    client?.project_types ?? [],
  );
  const [yearBuiltMin, setYearBuiltMin] = useState<string>(
    client?.year_built_min?.toString() ?? "",
  );
  const [yearBuiltMax, setYearBuiltMax] = useState<string>(
    client?.year_built_max?.toString() ?? "",
  );
  const [requiredFeatures, setRequiredFeatures] = useState<string[]>(
    client?.required_features ?? [],
  );
  const [requiredAmenities, setRequiredAmenities] = useState<string[]>(
    client?.required_amenities ?? [],
  );
  const [preferredFeatures, setPreferredFeatures] = useState<string[]>(
    client?.preferred_features ?? [],
  );
  const [preferredAmenities, setPreferredAmenities] = useState<string[]>(
    client?.preferred_amenities ?? [],
  );
  const [centerLat, setCenterLat] = useState<string>(client?.center_lat ?? "");
  const [centerLon, setCenterLon] = useState<string>(client?.center_lon ?? "");
  const [radiusKm, setRadiusKm] = useState<string>(client?.radius_km ?? "");
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(
    // Open by default if any of the new fields is already populated, so
    // a returning broker doesn't have to hunt for their saved settings.
    !!(
      client?.bathrooms_min ||
      client?.bathrooms_max ||
      client?.project_types?.length ||
      client?.year_built_min ||
      client?.year_built_max ||
      client?.required_features?.length ||
      client?.required_amenities?.length ||
      client?.preferred_features?.length ||
      client?.preferred_amenities?.length ||
      client?.center_lat ||
      client?.center_lon ||
      client?.radius_km
    ),
  );

  const [matches, setMatches] = useState<MatchWithOffer[] | null>(null);
  const [placementMatches, setPlacementMatches] = useState<
    PlacementMatchWithPlacement[] | null
  >(null);
  // Filter the "Matched offers" feed by who posted the listing. Only the
  // hot-offer feed has owner_type — placements are always the agency's own.
  const [postedBy, setPostedBy] = useState<"any" | "owner" | "agent">("any");
  // Mirrored as a ref so the long-lived polling tick can read the latest
  // value without re-subscribing to it as an effect dependency (which
  // would tear down + restart the polling loop on every filter flip).
  const postedByRef = useRef(postedBy);
  useEffect(() => {
    postedByRef.current = postedBy;
  }, [postedBy]);
  // True while we're still polling the placement-matches feed — the AI pass
  // runs asynchronously on client save, so results land over the next few
  // seconds. We stop polling as soon as at least one match arrives or the
  // poll window elapses.
  const [aiAnalyzing, setAiAnalyzing] = useState(false);

  // Load both match feeds on edit. The hot-offer matches come from scraped
  // portals; the placement matches are AI-scored against the agency's own
  // published listings. Placement matches poll for a bit because the AI
  // sweep that fills them runs in the background after save.
  useEffect(() => {
    if (!client) {
      setMatches(null);
      setPlacementMatches(null);
      setAiAnalyzing(false);
      return;
    }
    // Rematch populates BOTH the placement-matches feed (agency's own
    // listings) and the hot-offer matches feed (external, top-20 capped).
    // Poll both in lock-step so the broker sees updates land together.
    let cancelled = false;
    let tries = 0;
    const MAX_TRIES = 10; // ~25s — hot offer scoring is slower than placements
    let timer: ReturnType<typeof setTimeout> | null = null;
    setAiAnalyzing(true);

    const tick = async () => {
      if (cancelled) return;
      // Read the live posted_by via the ref so a filter toggle while the
      // polling loop is still ticking doesn't deliver stale results that
      // race with the dedicated postedBy refetch effect below.
      const live = postedByRef.current;
      const hotQs = live === "any" ? "" : `?posted_by=${live}`;
      try {
        const [pm, hm] = await Promise.all([
          api<{ items: PlacementMatchWithPlacement[] }>(
            `/api/clients/${client.id}/placement-matches`,
          ),
          api<{ items: MatchWithOffer[] }>(
            `/api/clients/${client.id}/matches${hotQs}`,
          ),
        ]);
        if (cancelled) return;
        setPlacementMatches(pm.items);
        setMatches(hm.items);
        tries += 1;
        // Stop once either feed has results OR we've exhausted the window.
        // Both feeds land when the background rematch completes; the first
        // non-empty response is the signal.
        const anyResults = pm.items.length > 0 || hm.items.length > 0;
        if (anyResults || tries >= MAX_TRIES) {
          setAiAnalyzing(false);
          return;
        }
      } catch {
        tries += 1;
        if (tries >= MAX_TRIES) {
          setAiAnalyzing(false);
          return;
        }
      }
      timer = setTimeout(tick, 2500);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Intentionally NOT depending on postedBy — that gets its own effect
    // below so toggling the filter doesn't restart the placement-match
    // polling loop.
  }, [client]);

  // Refetch only the hot-offer feed when the posted_by toggle changes.
  // Skips the polling loop since the rows are already in the DB at this
  // point; the polling effect above only matters during the post-save AI
  // sweep, not for filter changes.
  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    const hotQs = postedBy === "any" ? "" : `?posted_by=${postedBy}`;
    api<{ items: MatchWithOffer[] }>(
      `/api/clients/${client.id}/matches${hotQs}`,
    )
      .then((hm) => {
        if (!cancelled) setMatches(hm.items);
      })
      .catch(() => {
        // Non-fatal — the polling effect retries on its own cadence.
      });
    return () => {
      cancelled = true;
    };
  }, [client, postedBy]);

  const showBrokerSelect =
    viewerRole === "executive_manager" || viewerRole === "lead_broker";

  function togglePropertyType(t: string) {
    setPropertyTypes((prev) =>
      prev.includes(t) ? prev.filter((p) => p !== t) : [...prev, t],
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {
        full_name: fullName.trim(),
      };
      if (phone.trim()) body.phone = phone.trim();
      else if (isEdit) body.phone = null;
      if (email.trim()) body.email = email.trim();
      else if (isEdit) body.email = null;
      if (source.trim()) body.source = source.trim();
      else if (isEdit) body.source = null;
      if (notes.trim()) body.notes = notes.trim();
      else if (isEdit) body.notes = null;
      body.status = status;
      if (showBrokerSelect) {
        body.assigned_user_id = assignedUserId || null;
      }
      body.deal_type = dealType || null;
      body.rooms_min = roomsMin ? Number(roomsMin) : null;
      body.rooms_max = roomsMax ? Number(roomsMax) : null;
      body.bedrooms_min = bedroomsMin ? Number(bedroomsMin) : null;
      body.bedrooms_max = bedroomsMax ? Number(bedroomsMax) : null;
      body.floor_min = floorMin !== "" ? Number(floorMin) : null;
      body.floor_max = floorMax !== "" ? Number(floorMax) : null;
      body.price_min = priceMin ? Number(priceMin) : null;
      body.price_max = priceMax ? Number(priceMax) : null;
      body.currency = currency || null;
      body.area_min = areaMin ? Number(areaMin) : null;
      body.area_max = areaMax ? Number(areaMax) : null;
      body.property_types = propertyTypes.length ? propertyTypes : null;
      // Merge cascading-picker selections with any legacy free-text codes.
      // The matcher accepts both admin-region codes (fan out at match
      // time) and specific area codes — a client can have either or
      // both in the same selection.
      const mergedDistricts = [
        ...districtAdminCodes,
        ...areaCodes,
        ...legacyDistricts,
      ];
      body.districts = mergedDistricts.length ? mergedDistricts : null;
      body.streets = streetIds.size ? Array.from(streetIds) : null;
      body.building_statuses = buildingStatuses.length ? buildingStatuses : null;
      body.conditions = conditions.length ? conditions : null;

      // Advanced match dimensions — null when blank so the matcher
      // treats them as "no preference".
      body.bathrooms_min = bathroomsMin ? Number(bathroomsMin) : null;
      body.bathrooms_max = bathroomsMax ? Number(bathroomsMax) : null;
      body.project_types = projectTypes.length ? projectTypes : null;
      body.year_built_min = yearBuiltMin ? Number(yearBuiltMin) : null;
      body.year_built_max = yearBuiltMax ? Number(yearBuiltMax) : null;
      body.required_features = requiredFeatures.length ? requiredFeatures : null;
      body.required_amenities = requiredAmenities.length ? requiredAmenities : null;
      body.preferred_features = preferredFeatures.length ? preferredFeatures : null;
      body.preferred_amenities = preferredAmenities.length ? preferredAmenities : null;
      body.center_lat = centerLat ? Number(centerLat) : null;
      body.center_lon = centerLon ? Number(centerLon) : null;
      body.radius_km = radiusKm ? Number(radiusKm) : null;

      let saved: Client;
      if (isEdit) {
        saved = await api<Client>(`/api/clients/${client!.id}`, {
          method: "PATCH",
          json: body,
        });
      } else {
        saved = await api<Client>("/api/clients", { method: "POST", json: body });
      }
      await onSaved(saved, !isEdit);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 p-3 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Form caps its own height and scrolls internally so the header stays
          pinned to the top and the action buttons to the bottom — on a short
          viewport the middle section scrolls, instead of the whole form
          overflowing the page and clipping the title. */}
      <form
        className="card w-full max-w-2xl flex flex-col max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)]"
        onSubmit={submit}
      >
        <div className="px-6 pt-6 pb-4 border-b border-white/5 flex-shrink-0">
          <h2 className="card-title">
            {isEdit ? "Edit client" : "New client"}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Preferences drive auto-match against the Hot Offers feed.
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto py-4 space-y-5">

        <div className="px-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <div className="label">Full name *</div>
            <input
              className="input"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div>
            <div className="label">Phone</div>
            <input
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="599 12 34 56  ·  +393287236309"
            />
            {/* Live hint: shows what the backend will normalize the
                input to. Defaults the country to Georgia for bare
                9-digit numbers; foreign contacts work too when prefixed
                with +. Hint stays out of the way for empty / unchanged
                input so the form doesn't shout at the user. */}
            <PhoneHint raw={phone} />
          </div>
          <div>
            <div className="label">Email</div>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <div className="label">Source</div>
            <input
              className="input"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="referral, website, Instagram…"
            />
          </div>
          <div>
            <div className="label">Status</div>
            <select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as ClientStatus)}
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          {showBrokerSelect && (
            <div className="sm:col-span-2">
              <div className="label">Assigned broker</div>
              <select
                className="input"
                value={assignedUserId}
                onChange={(e) => setAssignedUserId(e.target.value)}
              >
                <option value="">— unassigned pool —</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.full_name || m.email} ({roleLabel(m.role)})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="px-6 pt-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Preferences
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="label">Deal type</div>
              <select
                className="input"
                value={dealType}
                onChange={(e) =>
                  setDealType(e.target.value as "" | "sale" | "rent")
                }
              >
                <option value="">Any</option>
                <option value="sale">Sale</option>
                <option value="rent">Rent</option>
              </select>
            </div>
            <div>
              <div className="label">Currency</div>
              <select
                className="input"
                value={currency}
                onChange={(e) =>
                  setCurrency(e.target.value as "" | "USD" | "GEL" | "EUR")
                }
              >
                <option value="">Any</option>
                <option value="USD">USD</option>
                <option value="GEL">GEL</option>
                <option value="EUR">EUR</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label">Min rooms</div>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={20}
                  value={roomsMin}
                  onChange={(e) => setRoomsMin(e.target.value)}
                />
              </div>
              <div>
                <div className="label">Max rooms</div>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={20}
                  value={roomsMax}
                  onChange={(e) => setRoomsMax(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label">Min bedrooms</div>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={20}
                  value={bedroomsMin}
                  onChange={(e) => setBedroomsMin(e.target.value)}
                />
              </div>
              <div>
                <div className="label">Max bedrooms</div>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={20}
                  value={bedroomsMax}
                  onChange={(e) => setBedroomsMax(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label">Min floor</div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={200}
                  value={floorMin}
                  onChange={(e) => setFloorMin(e.target.value)}
                  placeholder="0 = ground"
                />
              </div>
              <div>
                <div className="label">Max floor</div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={200}
                  value={floorMax}
                  onChange={(e) => setFloorMax(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label">Min price</div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                />
              </div>
              <div>
                <div className="label">Max price</div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label">Min area (m²)</div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={areaMin}
                  onChange={(e) => setAreaMin(e.target.value)}
                />
              </div>
              <div>
                <div className="label">Max area (m²)</div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={areaMax}
                  onChange={(e) => setAreaMax(e.target.value)}
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <div className="label">Property types</div>
              <div className="flex flex-wrap gap-2">
                {PROPERTY_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => togglePropertyType(t)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition ${
                      propertyTypes.includes(t)
                        ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                        : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/[0.05]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <LocationPicker
                districtAdminCodes={districtAdminCodes}
                areaCodes={areaCodes}
                streetIds={streetIds}
                onChange={(adminCodes, areas) => {
                  setDistrictAdminCodes(adminCodes);
                  setAreaCodes(areas);
                }}
                onChangeStreets={setStreetIds}
                legacyDistricts={legacyDistricts}
                onClearLegacy={() => setLegacyDistricts([])}
              />
            </div>
            <div className="sm:col-span-2">
              <div className="label">Building status (სტატუსი)</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(BUILDING_STATUS_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setBuildingStatuses((prev) =>
                        prev.includes(key)
                          ? prev.filter((k) => k !== key)
                          : [...prev, key],
                      )
                    }
                    className={`px-3 py-1.5 text-xs rounded-md border transition ${
                      buildingStatuses.includes(key)
                        ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                        : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/[0.05]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="label">Condition (მდგომარეობა)</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(CONDITION_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setConditions((prev) =>
                        prev.includes(key)
                          ? prev.filter((k) => k !== key)
                          : [...prev, key],
                      )
                    }
                    className={`px-3 py-1.5 text-xs rounded-md border transition ${
                      conditions.includes(key)
                        ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                        : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/[0.05]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* ── Advanced match preferences ───────────────────────
                Bathrooms, project type, year built, must-have / nice-to-
                have flags, and a geo radius. Collapsed by default to
                keep the form short for the common case; auto-opens when
                any of the fields is already saved. */}
            <div className="sm:col-span-2 border-t border-white/5 pt-4 mt-1">
              <button
                type="button"
                onClick={() => setAdvancedOpen((o) => !o)}
                className="flex items-center justify-between w-full group"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 group-hover:text-slate-200 transition">
                  Advanced match preferences
                </span>
                <span className="text-[11px] text-slate-500 group-hover:text-slate-300 transition">
                  {advancedOpen ? "hide ▴" : "show ▾"}
                </span>
              </button>
              <p className="text-[11px] text-slate-600 mt-1">
                Powered by the structured fields the refresh-structured
                pass populates: bathrooms, project type, year built,
                features / amenities, coordinates.
              </p>
            </div>

            {advancedOpen && (
              <>
                <div>
                  <div className="label">Bathrooms (min)</div>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={10}
                    value={bathroomsMin}
                    onChange={(e) => setBathroomsMin(e.target.value)}
                  />
                </div>
                <div>
                  <div className="label">Bathrooms (max)</div>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={10}
                    value={bathroomsMax}
                    onChange={(e) => setBathroomsMax(e.target.value)}
                  />
                </div>

                <div>
                  <div className="label">Year built (from)</div>
                  <input
                    className="input"
                    type="number"
                    min={1800}
                    max={2100}
                    placeholder="e.g. 2010"
                    value={yearBuiltMin}
                    onChange={(e) => setYearBuiltMin(e.target.value)}
                  />
                </div>
                <div>
                  <div className="label">Year built (to)</div>
                  <input
                    className="input"
                    type="number"
                    min={1800}
                    max={2100}
                    value={yearBuiltMax}
                    onChange={(e) => setYearBuiltMax(e.target.value)}
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="label">Project type (პროექტის ტიპი)</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(PROJECT_TYPE_LABELS).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          setProjectTypes((prev) =>
                            prev.includes(key)
                              ? prev.filter((k) => k !== key)
                              : [...prev, key],
                          )
                        }
                        className={`px-3 py-1.5 text-xs rounded-md border transition ${
                          projectTypes.includes(key)
                            ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                            : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/[0.05]"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <div className="label flex items-center justify-between">
                    <span>Must-have features</span>
                    <span className="text-[10px] text-rose-300/70">
                      hard reject if missing
                    </span>
                  </div>
                  <ChipPicker
                    options={FEATURE_LABELS}
                    selected={requiredFeatures}
                    onToggle={(k) =>
                      setRequiredFeatures((prev) =>
                        prev.includes(k)
                          ? prev.filter((c) => c !== k)
                          : [...prev, k],
                      )
                    }
                    tone="hard"
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="label flex items-center justify-between">
                    <span>Must-have amenities</span>
                    <span className="text-[10px] text-rose-300/70">
                      hard reject if missing
                    </span>
                  </div>
                  <ChipPicker
                    options={AMENITY_LABELS}
                    selected={requiredAmenities}
                    onToggle={(k) =>
                      setRequiredAmenities((prev) =>
                        prev.includes(k)
                          ? prev.filter((c) => c !== k)
                          : [...prev, k],
                      )
                    }
                    tone="hard"
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="label flex items-center justify-between">
                    <span>Nice-to-have features</span>
                    <span className="text-[10px] text-emerald-300/70">
                      proportional credit
                    </span>
                  </div>
                  <ChipPicker
                    options={FEATURE_LABELS}
                    selected={preferredFeatures}
                    onToggle={(k) =>
                      setPreferredFeatures((prev) =>
                        prev.includes(k)
                          ? prev.filter((c) => c !== k)
                          : [...prev, k],
                      )
                    }
                    tone="soft"
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="label flex items-center justify-between">
                    <span>Nice-to-have amenities</span>
                    <span className="text-[10px] text-emerald-300/70">
                      proportional credit
                    </span>
                  </div>
                  <ChipPicker
                    options={AMENITY_LABELS}
                    selected={preferredAmenities}
                    onToggle={(k) =>
                      setPreferredAmenities((prev) =>
                        prev.includes(k)
                          ? prev.filter((c) => c !== k)
                          : [...prev, k],
                      )
                    }
                    tone="soft"
                  />
                </div>

                <div className="sm:col-span-2 border border-white/5 rounded-md p-3 bg-white/[0.01]">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="label !mb-0">Geographic anchor</span>
                    <span className="text-[10px] text-slate-500">
                      all three required to apply the radius filter
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[10px] text-slate-500 mb-1">Latitude</div>
                      <input
                        className="input"
                        type="number"
                        step="0.000001"
                        min={-90}
                        max={90}
                        placeholder="41.715"
                        value={centerLat}
                        onChange={(e) => setCenterLat(e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 mb-1">Longitude</div>
                      <input
                        className="input"
                        type="number"
                        step="0.000001"
                        min={-180}
                        max={180}
                        placeholder="44.827"
                        value={centerLon}
                        onChange={(e) => setCenterLon(e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 mb-1">Radius (km)</div>
                      <input
                        className="input"
                        type="number"
                        step="0.1"
                        min={0.1}
                        max={200}
                        placeholder="2.0"
                        value={radiusKm}
                        onChange={(e) => setRadiusKm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="sm:col-span-2">
              <div className="label">Notes</div>
              <textarea
                className="input min-h-[80px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Budget is flexible, prefers natural light, no first floor…"
              />
            </div>
          </div>
        </div>

        {isEdit && (
          <div className="px-6 pt-2">
            <div className="flex items-center justify-between mb-3">
              <Link
                to={`/clients/${client!.id}`}
                className="text-xs font-medium text-accent-300 hover:text-accent-200"
              >
                Open full match view →
              </Link>
              <span className="text-[10px] text-slate-600">
                cards · dismiss · dismissed toggle
              </span>
            </div>
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-accent-400 flex items-center gap-2">
                AI-matched from your listings{" "}
                {placementMatches && `(${placementMatches.length})`}
                {aiAnalyzing && (
                  <span className="inline-flex items-center gap-1.5 text-accent-300 normal-case tracking-normal font-normal">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse" />
                    <span>analyzing…</span>
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-600">
                scored by Gemini against this client's preferences
              </div>
            </div>
            {placementMatches === null ? (
              <Skeleton className="h-16" />
            ) : placementMatches.length === 0 ? (
              <div className="text-xs text-slate-500 bg-white/[0.02] border border-white/5 rounded-md p-3">
                {aiAnalyzing
                  ? "Scanning your published listings with Gemini… matches will appear here within a few seconds."
                  : "No placement matches yet. New publishes match automatically; the nightly safety net catches anything missed."}
              </div>
            ) : (
              <ul className="space-y-2 max-h-[220px] overflow-y-auto mb-4">
                {placementMatches.map((m) => (
                  <li
                    key={m.id}
                    className="bg-accent-500/[0.04] border border-accent-500/20 rounded-md p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <a
                          href={m.placement.published_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-slate-100 hover:text-accent-300 truncate block"
                        >
                          {m.placement.title ||
                            m.placement.published_url}
                        </a>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className="uppercase text-[10px] text-slate-600">
                            {m.placement.platform}
                          </span>
                          {m.placement.price && (
                            <span>
                              · {Number(m.placement.price).toLocaleString()}{" "}
                              {m.placement.currency}
                            </span>
                          )}
                          {m.placement.rooms != null && (
                            <span>· {m.placement.rooms} rm</span>
                          )}
                          {m.placement.district && (
                            <span>· {m.placement.district}</span>
                          )}
                        </div>
                        {m.ai_reason && (
                          <div className="text-[11px] text-accent-200/90 mt-1.5 italic leading-snug">
                            💡 {m.ai_reason}
                          </div>
                        )}
                      </div>
                      <span className="badge border-accent-500/40 bg-accent-500/15 text-accent-200 shrink-0">
                        {Math.round(Number(m.score))}%
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Matched offers {matches && `(${matches.length})`}
              </div>
              <PostedByToggle value={postedBy} onChange={setPostedBy} compact />
            </div>
            {matches === null ? (
              <Skeleton className="h-16" />
            ) : matches.length === 0 ? (
              <div className="text-xs text-slate-500 bg-white/[0.02] border border-white/5 rounded-md p-3">
                No matches yet — we'll ping Telegram as soon as one surfaces.
              </div>
            ) : (
              <ul className="space-y-2 max-h-[260px] overflow-y-auto">
                {matches.map((m) => (
                  <li
                    key={m.id}
                    className="bg-white/[0.02] border border-white/5 rounded-md p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <a
                          href={m.offer.published_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-slate-100 hover:text-accent-300 truncate block"
                        >
                          {m.offer.title || m.offer.published_url}
                        </a>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {m.offer.price && (
                            <span>
                              {Number(m.offer.price).toLocaleString()}{" "}
                              {m.offer.currency}
                            </span>
                          )}
                          {m.offer.rooms != null && (
                            <span>· {m.offer.rooms} rm</span>
                          )}
                          {m.offer.district && <span>· {m.offer.district}</span>}
                        </div>
                      </div>
                      <span className="badge border-accent-500/30 bg-accent-500/10 text-accent-300 shrink-0">
                        {Math.round(Number(m.score))}%
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {err && (
          <div className="mx-6 text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
            {err}
          </div>
        )}

        </div>

        <div className="px-6 py-4 border-t border-white/5 flex-shrink-0 flex items-center gap-2">
          {isEdit && (
            <button
              type="button"
              className="btn-secondary"
              onClick={async () => {
                if (!client) return;
                try {
                  // POST /rematch wipes both placement_matches AND
                  // client_matches server-side, then schedules two fresh
                  // background jobs (placements: all ≥95%; hot offers:
                  // top 20 to bound Gemini spend). The mount-effect poll
                  // loop will reload both feeds as results land.
                  await api(`/api/clients/${client.id}/rematch`, {
                    method: "POST",
                  });
                  setPlacementMatches([]);
                  setMatches([]);
                  setAiAnalyzing(true);
                  // Kick an immediate refresh so the empty-state message
                  // ("analyzing…") shows right away; the polling effect
                  // keeps refreshing until results appear or timeout.
                  const hotQs =
                    postedBy === "any" ? "" : `?posted_by=${postedBy}`;
                  const [pm, hm] = await Promise.all([
                    api<{ items: PlacementMatchWithPlacement[] }>(
                      `/api/clients/${client.id}/placement-matches`,
                    ),
                    api<{ items: MatchWithOffer[] }>(
                      `/api/clients/${client.id}/matches${hotQs}`,
                    ),
                  ]);
                  setPlacementMatches(pm.items);
                  setMatches(hm.items);
                } catch (e) {
                  setErr(e instanceof ApiError ? e.message : "Rematch failed");
                }
              }}
              disabled={busy}
              title="Clear and recompute AI matches against your current listings"
            >
              ✨ Rematch
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || !fullName.trim()}
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create client"}
          </button>
        </div>
      </form>
    </div>
  );
}

// Response shape of the AI-scored placement matches endpoint.
interface PlacementMatchWithPlacement {
  id: string;
  score: string;
  ai_reason: string | null;
  dismissed: boolean;
  notified_at: string | null;
  created_at: string;
  placement: {
    id: string;
    platform: "myhome" | "ss";
    published_url: string;
    title: string | null;
    price: string | null;
    currency: string | null;
    rooms: number | null;
    district: string | null;
    cover_photo_url: string | null;
  };
}

// Shape of the client matches endpoint (added next). We keep the interface
// inline because the page is the only consumer.
interface MatchWithOffer {
  id: string;
  score: string;
  dismissed: boolean;
  notified_at: string | null;
  created_at: string;
  offer: {
    id: string;
    platform: "myhome" | "ss";
    published_url: string;
    title: string | null;
    price: string | null;
    currency: string | null;
    rooms: number | null;
    district: string | null;
  };
}

/** Top-of-page funnel strip: 5 KPI tiles (Active → with deal → in
 *  pipeline → won 30d, plus Stale clients), two dwell-time chips, and
 *  a per-broker conversion mini-table. Skeleton until ``funnel``
 *  arrives so the layout doesn't reflow on first paint. */
function FunnelStrip({ funnel }: { funnel: ClientFunnel | null }) {
  if (!funnel) {
    return (
      <div className="space-y-3 mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[78px]" />
          ))}
        </div>
      </div>
    );
  }
  // Conversion percentages — guarded against 0 so the chip just hides.
  const pctWithDeal =
    funnel.active_clients > 0
      ? Math.round((funnel.clients_with_deal / funnel.active_clients) * 100)
      : null;
  const pctPipeline =
    funnel.active_clients > 0
      ? Math.round((funnel.clients_in_pipeline / funnel.active_clients) * 100)
      : null;
  const fmtDays = (n: number | null) =>
    n === null ? "—" : n < 1 ? "<1 day" : n < 2 ? "1 day" : `${Math.round(n)} days`;
  return (
    <div className="space-y-3 mb-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <FunnelTile label="Active clients" value={funnel.active_clients} />
        <FunnelTile
          label="With deal"
          value={funnel.clients_with_deal}
          hint={pctWithDeal !== null ? `${pctWithDeal}% of active` : undefined}
        />
        <FunnelTile
          label="In pipeline"
          value={funnel.clients_in_pipeline}
          hint={pctPipeline !== null ? `${pctPipeline}% of active` : undefined}
          tone="accent"
        />
        <FunnelTile
          label="Won · 30d"
          value={funnel.clients_won_30d}
          tone="emerald"
        />
        <FunnelTile
          label="Stale ≥14d"
          value={funnel.stale_clients}
          hint="active, no deal yet"
          tone={funnel.stale_clients > 0 ? "rose" : "neutral"}
        />
      </div>
      <div className="card p-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px]">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Client → first deal
          </span>
          <span className="text-slate-200 tabular-nums font-semibold">
            {fmtDays(funnel.avg_days_client_to_first_deal)}
          </span>
          <span className="text-slate-600">avg · 30d</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Booked → done
          </span>
          <span className="text-slate-200 tabular-nums font-semibold">
            {fmtDays(funnel.avg_days_booked_to_done)}
          </span>
          <span className="text-slate-600">avg · 90d</span>
        </div>
      </div>
      {funnel.per_broker.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 pt-3 pb-1.5 flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Per-broker · 30d
            </div>
            <div className="text-[10px] text-slate-600">
              conversion = won / deals started
            </div>
          </div>
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wider text-slate-600">
              <tr className="border-t border-white/5">
                <th className="text-left px-4 py-1.5 font-semibold">Broker</th>
                <th className="text-right px-3 py-1.5 font-semibold">Active</th>
                <th className="text-right px-3 py-1.5 font-semibold">Started</th>
                <th className="text-right px-3 py-1.5 font-semibold">Won</th>
                <th className="text-right px-4 py-1.5 font-semibold">Conv</th>
              </tr>
            </thead>
            <tbody>
              {funnel.per_broker.slice(0, 8).map((row) => {
                const rate = row.conversion_rate;
                const ratePct =
                  rate === null ? null : Math.round(rate * 100);
                const rateTone =
                  ratePct === null
                    ? "text-slate-600"
                    : ratePct >= 30
                      ? "text-emerald-300"
                      : ratePct >= 10
                        ? "text-amber-300"
                        : "text-slate-400";
                return (
                  <tr key={row.user_id} className="border-t border-white/5">
                    <td className="px-4 py-1.5 text-slate-200 truncate">
                      {row.name}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-300">
                      {row.active_clients}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                      {row.deals_started_30d}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                      {row.won_30d}
                    </td>
                    <td
                      className={`px-4 py-1.5 text-right tabular-nums font-semibold ${rateTone}`}
                    >
                      {ratePct === null ? "—" : `${ratePct}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FunnelTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: "neutral" | "accent" | "emerald" | "rose";
}) {
  const TONE: Record<NonNullable<typeof tone>, string> = {
    neutral: "border-white/5 bg-white/[0.02]",
    accent: "border-accent-500/30 bg-accent-500/[0.06]",
    emerald: "border-emerald-500/30 bg-emerald-500/[0.06]",
    rose: "border-rose-500/30 bg-rose-500/[0.06]",
  };
  const VAL_TONE: Record<NonNullable<typeof tone>, string> = {
    neutral: "text-slate-100",
    accent: "text-accent-200",
    emerald: "text-emerald-200",
    rose: "text-rose-200",
  };
  return (
    <div className={`card p-3 border ${TONE[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className={`text-2xl font-semibold tabular-nums leading-tight mt-0.5 ${VAL_TONE[tone]}`}
      >
        {value}
      </div>
      {hint && (
        <div className="text-[10.5px] text-slate-500 mt-0.5">{hint}</div>
      )}
    </div>
  );
}

/** Live phone-number normalization preview. Mirrors the basic shape of
 *  the backend's app.services.phone.normalize_phone — bare 9-digit
 *  Georgian mobiles get a +995 prefix, 00... becomes +..., and
 *  numbers that already carry a + pass through unchanged after
 *  stripping non-digits. Doesn't try to fully validate per-country
 *  metadata (that's libphonenumber's job, server-side); just shows the
 *  broker what the saved value will probably look like so a typo
 *  (5991234 — missing a digit) is obvious before submit.
 *
 *  Returns nothing when the input is empty / unchanged so the form
 *  doesn't shout at the user. */
function PhoneHint({ raw }: { raw: string }) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const probable = guessNormalizedPhone(trimmed);
  if (!probable) {
    return (
      <div className="text-[10.5px] text-amber-300/85 mt-1">
        Can't recognise this as a phone number — server will re-check on save.
      </div>
    );
  }
  // No hint when input already equals the normalized form (modulo spacing).
  if (probable.replace(/\s|-/g, '') === trimmed.replace(/\s|-/g, '')) return null;
  return (
    <div className="text-[10.5px] text-slate-500 mt-1">
      Will save as <span className="text-slate-300 font-mono">{probable}</span>
    </div>
  );
}

/** Heuristic frontend mirror of the backend normalizer. Good enough to
 *  preview; the backend has the final say. */
function guessNormalizedPhone(raw: string): string | null {
  const cleaned = raw.trim();
  if (!cleaned) return null;
  // Already international — keep as-is, just strip spaces/dashes.
  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1).replace(/\D/g, '');
    return digits.length >= 7 ? '+' + digits : null;
  }
  // European 00 prefix → +.
  if (cleaned.startsWith('00')) {
    const digits = cleaned.slice(2).replace(/\D/g, '');
    return digits.length >= 7 ? '+' + digits : null;
  }
  const digits = cleaned.replace(/\D/g, '');
  if (!digits) return null;
  // Georgian 995 prefix without +.
  if (digits.startsWith('995') && digits.length >= 12) {
    return '+' + digits;
  }
  // Bare 9-digit Georgian mobile (5XX XXX XXX) or landline.
  if (digits.length === 9) {
    return '+995' + digits;
  }
  if (digits.length === 7) {
    // Old Tbilisi 7-digit landline (2 XX XX XX) → +995 32 XX XX XX
    // We can't safely guess the area code, so just don't preview.
    return null;
  }
  return null;
}

